import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Clock,
  Settings,
  ExternalLink,
  Puzzle,
  Palette,
  Key,
  Timer,
  Activity,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Layout from "../components/layout/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SitesPage = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [checkingSite, setCheckingSite] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    site_name: "",
    site_url: "",
    site_type: "wordpress",
    wp_username: "",
    wp_app_password: "",
    use_plugin_endpoint: false,
    plugin_api_key: "",
    check_interval: 15,
  });

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data);
    } catch (err) {
      toast.error("Failed to load sites");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const resetForm = () => {
    setFormData({
      site_name: "",
      site_url: "",
      site_type: "wordpress",
      wp_username: "",
      wp_app_password: "",
      use_plugin_endpoint: false,
      plugin_api_key: "",
      check_interval: 15,
    });
    setSelectedSite(null);
  };

  const handleOpenDialog = (site = null) => {
    if (site) {
      setSelectedSite(site);
      setFormData({
        site_name: site.site_name,
        site_url: site.site_url,
        site_type: site.site_type || "wordpress",
        wp_username: site.wp_username || "",
        wp_app_password: "",
        use_plugin_endpoint: site.use_plugin_endpoint || false,
        plugin_api_key: "",
        check_interval: site.check_interval || 15,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedSite) {
        // Update existing site
        const updateData = { ...formData };
        // Only send password if it was changed
        if (!updateData.wp_app_password) {
          delete updateData.wp_app_password;
        }
        if (!updateData.plugin_api_key) {
          delete updateData.plugin_api_key;
        }
        await axios.put(`${API}/sites/${selectedSite.site_id}`, updateData);
        toast.success("Site updated successfully");
      } else {
        // Create new site
        await axios.post(`${API}/sites`, formData);
        toast.success("Site added successfully");
      }
      handleCloseDialog();
      fetchSites();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save site");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSite) return;

    try {
      await axios.delete(`${API}/sites/${selectedSite.site_id}`);
      toast.success("Site deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedSite(null);
      fetchSites();
    } catch (error) {
      toast.error("Failed to delete site");
    }
  };

  const handleCheckSite = async (siteId) => {
    setCheckingSite(siteId);
    try {
      const response = await axios.post(`${API}/sites/${siteId}/check`);
      if (response.data.is_online) {
        toast.success("Site is online and responding");
      } else {
        toast.error(response.data.error || "Site is not responding");
      }
      fetchSites();
    } catch (error) {
      toast.error("Failed to check site status");
    } finally {
      setCheckingSite(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading sites...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="sites-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-50 font-['Outfit'] tracking-tight">
              Sites
            </h1>
            <p className="text-zinc-500 mt-1">
              Manage your monitored WordPress sites
            </p>
          </div>
          <Button
            data-testid="add-site-btn"
            onClick={() => handleOpenDialog()}
            className="sheriff-btn-primary h-10 px-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Site
          </Button>
        </div>

        {/* Sites Grid */}
        {sites.length === 0 ? (
          <Card className="sheriff-card">
            <CardContent className="p-12 text-center">
              <Globe className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-50 mb-2">
                No sites added yet
              </h3>
              <p className="text-zinc-500 mb-6 max-w-md mx-auto">
                Start monitoring your WordPress sites by adding them here. You can
                track uptime, updates, and receive alerts.
              </p>
              <Button
                onClick={() => handleOpenDialog()}
                className="sheriff-btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Site
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sites.map((site) => (
              <Card
                key={site.site_id}
                data-testid={`site-card-${site.site_id}`}
                className="sheriff-card site-card"
              >
                <CardContent className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`status-dot ${
                          site.is_online ? "status-dot-online" : "status-dot-offline"
                        }`}
                      ></div>
                      <div>
                        <h3 className="font-semibold text-slate-50">{site.site_name}</h3>
                        <a
                          href={site.site_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-zinc-500 hover:text-amber-500 flex items-center gap-1 transition-colors"
                        >
                          {site.site_url.replace(/^https?:\/\//, "").slice(0, 30)}
                          {site.site_url.length > 30 && "..."}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={site.is_online ? "badge-online" : "badge-offline"}
                    >
                      {site.is_online ? "Online" : "Offline"}
                    </Badge>
                  </div>

                  {/* Stats */}
                  {(site.site_type || "wordpress") === "wordpress" ? (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-zinc-950 rounded-md p-3 text-center">
                      <p className="text-xs text-zinc-500 mb-1">WP Version</p>
                      <p className="font-mono text-sm text-amber-500">
                        {site.wp_version || "N/A"}
                      </p>
                    </div>
                    <div className="bg-zinc-950 rounded-md p-3 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Puzzle className="w-3 h-3 text-zinc-500" />
                        <p className="text-xs text-zinc-500">Plugins</p>
                      </div>
                      <p className="font-mono text-sm text-slate-50">
                        {site.plugin_updates || 0}
                      </p>
                    </div>
                    <div className="bg-zinc-950 rounded-md p-3 text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Palette className="w-3 h-3 text-zinc-500" />
                        <p className="text-xs text-zinc-500">Themes</p>
                      </div>
                      <p className="font-mono text-sm text-slate-50">
                        {site.theme_updates || 0}
                      </p>
                    </div>
                  </div>
                  ) : (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-zinc-950 rounded-md p-3 text-center">
                      <p className="text-xs text-zinc-500 mb-1">HTTP Status</p>
                      <p className="font-mono text-sm text-amber-500">
                        {site.http_status || (site.is_online ? "200" : "—")}
                      </p>
                    </div>
                    <div className="bg-zinc-950 rounded-md p-3 text-center">
                      <p className="text-xs text-zinc-500 mb-1">Response</p>
                      <p className="font-mono text-sm text-slate-50">
                        {site.response_time_ms ? `${site.response_time_ms}ms` : "—"}
                      </p>
                    </div>
                  </div>
                  )}

                  {/* Error Message */}
                  {site.last_error && (
                    <div className="bg-red-950/20 border border-red-900/30 rounded-md p-3 mb-4">
                      <p className="text-sm text-red-400">{site.last_error}</p>
                    </div>
                  )}

                  {/* Monitoring Info */}
                  <div className="flex items-center justify-between text-xs text-zinc-600 mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {site.last_checked 
                        ? `Last: ${new Date(site.last_checked).toLocaleString()}`
                        : "Not checked yet"}
                    </div>
                    <div className="flex items-center gap-1">
                      <Timer className="w-3 h-3 text-amber-500" />
                      <span className="text-amber-500">Every {site.check_interval || 15}m</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t border-zinc-800">
                    <Button
                      data-testid={`check-site-btn-${site.site_id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => handleCheckSite(site.site_id)}
                      disabled={checkingSite === site.site_id}
                      className="flex-1 sheriff-btn-outline"
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${
                          checkingSite === site.site_id ? "animate-spin" : ""
                        }`}
                      />
                      {checkingSite === site.site_id ? "Checking..." : "Check Now"}
                    </Button>
                    <Button
                      data-testid={`edit-site-btn-${site.site_id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(site)}
                      className="sheriff-btn-ghost"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`delete-site-btn-${site.site_id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedSite(site);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-slate-50">
                {selectedSite ? "Edit Site" : "Add New Site"}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {selectedSite
                  ? "Update the site configuration below."
                  : "Enter the details for the site you want to monitor."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="site_type" className="text-zinc-400">
                    Site Type
                  </Label>
                  <Select
                    value={formData.site_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, site_type: value })
                    }
                  >
                    <SelectTrigger
                      data-testid="site-type-select"
                      className="bg-zinc-950 border-zinc-800 text-slate-50"
                    >
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="wordpress">WordPress</SelectItem>
                      <SelectItem value="generic">Generic Website</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="site_name" className="text-zinc-400">
                    Site Name
                  </Label>
                  <Input
                    id="site_name"
                    data-testid="site-name-input"
                    value={formData.site_name}
                    onChange={(e) =>
                      setFormData({ ...formData, site_name: e.target.value })
                    }
                    placeholder={formData.site_type === "generic" ? "My Website" : "My WordPress Site"}
                    className="sheriff-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="site_url" className="text-zinc-400">
                    Site URL
                  </Label>
                  <Input
                    id="site_url"
                    data-testid="site-url-input"
                    value={formData.site_url}
                    onChange={(e) =>
                      setFormData({ ...formData, site_url: e.target.value })
                    }
                    placeholder="https://example.com"
                    className="sheriff-input"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="check_interval" className="text-zinc-400">
                    Check Interval
                  </Label>
                  <Select
                    value={String(formData.check_interval)}
                    onValueChange={(value) =>
                      setFormData({ ...formData, check_interval: parseInt(value) })
                    }
                  >
                    <SelectTrigger 
                      data-testid="check-interval-select"
                      className="bg-zinc-950 border-zinc-800 text-slate-50"
                    >
                      <SelectValue placeholder="Select interval" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="5">Every 5 minutes</SelectItem>
                      <SelectItem value="15">Every 15 minutes (default)</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every 60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-600">
                    How often to automatically check this site
                  </p>
                </div>

                {formData.site_type === "wordpress" && (
                <>
                <div className="pt-4 border-t border-zinc-800">
                  <p className="text-sm font-medium text-slate-50 mb-3">
                    WordPress REST API Credentials (Optional)
                  </p>
                  <p className="text-xs text-zinc-500 mb-4">
                    For advanced monitoring features like plugin/theme updates
                  </p>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="wp_username" className="text-zinc-400">
                        WordPress Username
                      </Label>
                      <Input
                        id="wp_username"
                        data-testid="wp-username-input"
                        value={formData.wp_username}
                        onChange={(e) =>
                          setFormData({ ...formData, wp_username: e.target.value })
                        }
                        placeholder="admin"
                        className="sheriff-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="wp_app_password" className="text-zinc-400">
                        Application Password
                      </Label>
                      <Input
                        id="wp_app_password"
                        data-testid="wp-password-input"
                        type="password"
                        value={formData.wp_app_password}
                        onChange={(e) =>
                          setFormData({ ...formData, wp_app_password: e.target.value })
                        }
                        placeholder={selectedSite ? "Leave blank to keep current" : "xxxx xxxx xxxx xxxx"}
                        className="sheriff-input"
                      />
                      <p className="text-xs text-zinc-600">
                        Generate at: WP Admin → Users → Profile → Application Passwords
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm font-medium text-slate-50">
                        Use Custom Plugin Endpoint
                      </p>
                      <p className="text-xs text-zinc-500">
                        For sites with WP Sheriff plugin installed
                      </p>
                    </div>
                    <Switch
                      data-testid="plugin-endpoint-switch"
                      checked={formData.use_plugin_endpoint}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, use_plugin_endpoint: checked })
                      }
                    />
                  </div>

                  {formData.use_plugin_endpoint && (
                    <div className="space-y-2">
                      <Label htmlFor="plugin_api_key" className="text-zinc-400">
                        Plugin API Key
                      </Label>
                      <Input
                        id="plugin_api_key"
                        data-testid="plugin-key-input"
                        type="password"
                        value={formData.plugin_api_key}
                        onChange={(e) =>
                          setFormData({ ...formData, plugin_api_key: e.target.value })
                        }
                        placeholder={selectedSite ? "Leave blank to keep current" : "Enter API key"}
                        className="sheriff-input"
                      />
                    </div>
                  )}
                </div>
                </>
                )}
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                  className="sheriff-btn-outline"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  data-testid="save-site-btn"
                  disabled={saving}
                  className="sheriff-btn-primary"
                >
                  {saving ? "Saving..." : selectedSite ? "Update Site" : "Add Site"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-50">
                Delete Site
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete "{selectedSite?.site_name}"? This
                action cannot be undone and will also delete all related alerts.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="sheriff-btn-outline">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-delete-btn"
                onClick={handleDelete}
                className="sheriff-btn-destructive"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default SitesPage;
